/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2021 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import React, { FC, useMemo } from 'react'

import {
  ActEventFlow,
  ActForm,
  getShortName,
  readNotifications,
} from '@builddocs/data-management'
import { getAOSRById, getSecondaryActById } from '@builddocs/filestore-api'
import { useToast } from '@builddocs/system'
import { Chip, Stack } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import { format, parseISO } from 'date-fns'
import { ru } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'

import type { ActNotification } from '@builddocs/data-management'

interface OwnProps {
  notification: ActNotification
  onClosePopover: () => void
  refetch: () => void
}

type Props = OwnProps

/**
 * Элемент списка уведомлений для события создания акта
 * @param {React.PropsWithChildren<Props>} props
 * @returns {JSX.Element | null}
 * @constructor
 */
export const BDNotificationAct: FC<Props> = props => {
  const { notification, onClosePopover, refetch } = props

  const navigate = useNavigate()

  const { toast } = useToast()

  const {
    actEvent,
    notificationTargetId,
    createDateTime,
    generator,
    viewed,
    businessKey,
  } = notification

  const urlMatches = /projects\/([\d\w-]+)\/contracts\/([\d\w-]+)\//gi.exec(
    actEvent.actLocationUrl,
  )

  const eventProjectId = urlMatches?.[1]
  const eventContractId = urlMatches?.[2]

  const actDateString = format(parseISO(actEvent.actDate), 'PP', {
    locale: ru,
  })

  const isDeleted = useMemo(
    () => notification.actEvent?.actualState === 'DELETED',
    [notification.actEvent?.actualState],
  )

  const goToActPage = async () => {
    let searchParams: string

    if (isDeleted) return

    if (notification.actEvent.actualState) {
      const { workDocSection, actualState, closingId, workType } =
        notification.actEvent

      if (isDeleted) {
        toast.error('Файл был удалён')
        return
      }

      const tab = actualState === 'IN_CLOSING' ? 'closings' : 'drafts'

      searchParams = `?select=${tab}${
        actualState === 'IN_CLOSING' ? '&closingId=' + closingId : ''
      }&workType=${workType}&actId=${businessKey}&workDocSection=${workDocSection}&open`
    } else {
      const act =
        notification.actEvent.actAbbreviation === ActForm.AOSR
          ? await getAOSRById(businessKey)
          : await getSecondaryActById(businessKey)

      if (!act) {
        toast.error('Не удалось получить акт')
        return
      }
      const { workType, id, closingId, workDocSection, deleted } = act

      if (deleted) {
        toast.error('Файл был удалён')
        return
      }
      const tab = closingId ? 'closings' : 'drafts'

      searchParams = `?select=${tab}${
        closingId ? '&closingId=' + closingId : ''
      }&workType=${workType}&actId=${id}&workDocSection=${workDocSection}&open`
    }

    if (eventProjectId && eventContractId) {
      onClosePopover()
      readNotifications([notificationTargetId]).then(() => refetch())

      navigate({
        pathname: `/projects/${notification.actEvent.projectId}/contracts/${notification.actEvent.journalId}/acts`,
        search: `${searchParams}`,
      })
    }
  }

  const formattedText = () => {
    switch (actEvent.actEventFlow) {
      case ActEventFlow.NEW:
        return actEvent.closed
          ? 'добавил в закрытие'
          : 'сформировал черновик акта'
      case ActEventFlow.SENT_TO_APPROVAL:
        return 'отправил на согласование'
      case ActEventFlow.USER_APPROVED:
        return 'согласовал'
      case ActEventFlow.LAST_USER_APPROVED:
        return 'завершил согласование'
      case ActEventFlow.APPROVAL_TO_REJECTED:
        return 'не согласовал'
      case ActEventFlow.SIGNING_TO_REJECTED:
        return 'не подписал'
      case ActEventFlow.RECREATED:
        return 'переформировал'
      case ActEventFlow.SENT_TO_SIGN:
        return 'отправил на подписание'
      case ActEventFlow.USER_SIGNED:
        return 'подписал'
      case ActEventFlow.LAST_USER_SIGNED:
        return 'завершил подписание'
      default:
        return 'произвел действие с'
    }
  }

  const [avatar] = React.useState<string>('')

  // React.useEffect(() => {
  //   getAvatarSource(generatorId).then(avatar => setAvatar(avatar))
  // }, [])

  return (
    <Stack
      alignItems="center"
      direction="row"
      spacing={2}
      sx={{ opacity: isDeleted ? 0.5 : 1 }}
      onClick={actEvent.actEventFlow && goToActPage}
    >
      <Avatar alt={getShortName(generator)} src={avatar} />
      <ListItemText
        disableTypography
        primary={
          <>
            {isDeleted && (
              <Chip
                color="error"
                label="Удалено"
                sx={{ height: 24, mr: 1, cursor: 'pointer' }}
              />
            )}
            <Typography
              component="span"
              fontWeight={viewed ? 'normal' : 'bold'}
              whiteSpace="break-spaces"
            >
              {actEvent.actEventFlow
                ? `${getShortName(generator)} ${formattedText()}${
                    actEvent.actEventFlow === ActEventFlow.NEW &&
                    !actEvent.closed
                      ? ''
                      : ` акт №${actEvent.actNumber} от ${actDateString}`
                  }`
                : `${getShortName(generator)} совершил неизвестное действие`}
            </Typography>
          </>
        }
        secondary={
          actEvent.actEventFlow && (
            <>
              <Typography color="text.secondary" whiteSpace="break-spaces">
                {`в разделе ${actEvent.workDocSection} объекта ${actEvent.journalTitle}`}
              </Typography>
              <Typography
                color="text.secondary"
                variant="caption"
                whiteSpace="nowrap"
              >
                {format(parseISO(createDateTime), 'p, PPP', {
                  locale: ru,
                })}
              </Typography>
            </>
          )
        }
      />
    </Stack>
  )
}
